import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, CheckCircle, Eye, EyeSlash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";

export default function ResetPasswordPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [show, setShow] = useState({ next: false, confirm: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Request a new one.");
  }, [token]);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) return;
    if (form.new_password.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      setError("New password and confirmation do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: form.new_password,
      });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "rounded-xl bg-zinc-50 border-zinc-200 focus:bg-white focus:border-[#007BFF] focus:ring-[#007BFF]/15 h-11 px-4 pr-10";

  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center px-4 py-10 sm:py-16">
      <nav className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-3xl bg-white/80 backdrop-blur-md rounded-full px-5 py-2.5 flex justify-between items-center shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-white/40">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <BrandLogo variant="compact" />
        </Link>
        <Link
          to="/login"
          className="text-xs sm:text-sm font-medium text-[#001E44] hover:text-[#007BFF] transition-colors"
          data-testid="nav-login-link"
        >
          Sign in →
        </Link>
      </nav>

      <div className="w-full max-w-md">
        {done ? (
          <div
            className="bg-white rounded-3xl border border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-10 text-center"
            data-testid="reset-success"
          >
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 mx-auto flex items-center justify-center">
              <CheckCircle size={28} weight="bold" />
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-[#001E44] mt-5">
              Password updated
            </h2>
            <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
              You&apos;re all set. Redirecting you to sign in...
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 sm:p-10">
            <div className="space-y-2 mb-7">
              <span className="inline-block label-eyebrow text-[#007BFF]">Set new password</span>
              <h1 className="font-display text-3xl font-bold tracking-tight text-[#001E44] leading-tight">
                Choose a new password
              </h1>
              <p className="text-sm text-zinc-500 leading-relaxed pt-1">
                Pick something at least 8 characters long. You&apos;ll use this to sign in next time.
              </p>
            </div>

            {!token ? (
              <div className="rounded-xl bg-amber-50 border border-amber-100 text-amber-800 text-sm px-4 py-3 flex items-start gap-2" data-testid="reset-missing-token">
                <Warning size={18} weight="bold" className="shrink-0 mt-0.5" />
                <div>
                  This reset link is incomplete.{" "}
                  <Link to="/forgot-password" className="underline font-semibold" data-testid="link-request-new">
                    Request a new one →
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-zinc-700">New password</Label>
                  <div className="relative">
                    <Input
                      type={show.next ? "text" : "password"}
                      value={form.new_password}
                      onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
                      required
                      autoFocus
                      autoComplete="new-password"
                      data-testid="reset-new-password"
                      className={inputCls}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShow((s) => ({ ...s, next: !s.next }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-zinc-400 hover:text-[#001E44]"
                      aria-label={show.next ? "Hide password" : "Show password"}
                      data-testid="reset-new-password-toggle"
                    >
                      {show.next ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-zinc-700">Confirm new password</Label>
                  <div className="relative">
                    <Input
                      type={show.confirm ? "text" : "password"}
                      value={form.confirm}
                      onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                      required
                      autoComplete="new-password"
                      data-testid="reset-confirm-password"
                      className={inputCls}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShow((s) => ({ ...s, confirm: !s.confirm }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-zinc-400 hover:text-[#001E44]"
                      aria-label={show.confirm ? "Hide password" : "Show password"}
                      data-testid="reset-confirm-password-toggle"
                    >
                      {show.confirm ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div
                    className="rounded-xl bg-red-50 border border-red-100 text-red-700 text-xs font-medium px-3 py-2.5"
                    data-testid="reset-error"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[#007BFF] hover:bg-[#0062cc] rounded-full h-12 text-sm font-semibold shadow-sm hover:shadow-md transition-all"
                  data-testid="reset-submit"
                >
                  {busy ? "Saving..." : <>Update password <ArrowRight size={16} weight="bold" className="ml-2" /></>}
                </Button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
